import { z } from 'zod'
import type { ApiClient } from './client'

/**
 * MITRE ATT&CK coverage — Vantage Coverage's data.
 *
 * Shapes VERIFIED 2026-08-22 against the gotham scope, live
 * (docs/inventory/attack-capture.md). The model is a JOIN, same as legacy:
 * no endpoint returns a populated matrix —
 *
 *   attack/matrix           the tactic name skeleton, nothing else
 *   attack/analytics        technique name -> its detection content (2.4MB!)
 *   landingpage/coverage    the posture denominators (202 total techniques)
 *   get_mitre_technique_data  framework detail, by T-id ONLY
 *
 * Facts the capture established:
 * - Tactic names arrive DIRTY ("Defence Evasion" beside "Defense Evasion",
 *   "Initial access" beside "Initial Access") — canonicalizeTactic folds
 *   case and the British spelling before any join.
 * - Technique keys are NAMES; the detail endpoint requires a T-id, and a
 *   miss is still status:'success' with an empty result object.
 * - analytics enumerates only techniques WITH content, so coverage
 *   percentages must use landingpage/coverage's totals — the uncovered
 *   techniques cannot be listed from analytics.
 * - requires_stream is [[{is_data, stream}]]; group semantics unverified —
 *   read it only as "the streams this content reads".
 */

/* ------------------------------------------------------------------ schemas */

export const attackTacticsResponse = z.object({
  result: z.array(z.string()).default([]),
  status: z.string().optional(),
})

export interface Detection {
  id: string
  name: string
  tactic: string
  severity: string
  active: boolean
  disabled: boolean
  native: boolean
  is_latest: boolean
  type: string
  signal_count: number
  requires_stream: Array<Array<{ is_data: boolean; stream: string }>>
}

/**
 * Hand-rolled, NOT zod: the analytics payload is ~2.4MB / 6,543 detections
 * live, and a zod parse of it costs ~7 SECONDS on the main thread — then
 * throws anyway, because live carries `severity: null` (observed) and any
 * other field may go null next. This reader coalesces every field and drops
 * only detections with no name at all; it runs in milliseconds.
 */
export function readDetection(v: unknown): Detection | null {
  if (typeof v !== 'object' || v === null) return null
  const o = v as Record<string, unknown>
  if (typeof o.name !== 'string' || !o.name) return null
  const str = (x: unknown) => (typeof x === 'string' ? x : '')
  const bool = (x: unknown) => x === true
  const num = (x: unknown) => (typeof x === 'number' && Number.isFinite(x) ? x : 0)
  const groups = Array.isArray(o.requires_stream) ? o.requires_stream : []
  const requires_stream = groups
    .map((g) =>
      Array.isArray(g)
        ? g
            .map((e) => ({
              is_data: bool((e as Record<string, unknown> | null)?.is_data),
              stream: str((e as Record<string, unknown> | null)?.stream),
            }))
            .filter((e) => e.stream)
        : [],
    )
    .filter((g) => g.length)
  return {
    id: str(o.id),
    name: o.name,
    tactic: str(o.tactic),
    severity: str(o.severity),
    active: bool(o.active),
    disabled: bool(o.disabled),
    native: bool(o.native),
    is_latest: bool(o.is_latest),
    type: str(o.type),
    signal_count: num(o.signal_count),
    requires_stream,
  }
}

export function readAnalytics(json: unknown): Record<string, Detection[]> {
  const body =
    typeof json === 'object' && json !== null
      ? ((json as Record<string, unknown>).result ?? {})
      : {}
  const out: Record<string, Detection[]> = {}
  if (typeof body !== 'object' || body === null) return out
  for (const [tech, v] of Object.entries(body)) {
    if (!Array.isArray(v)) continue
    const dets = v.map(readDetection).filter((d): d is Detection => d !== null)
    if (dets.length) out[tech] = dets
  }
  return out
}

export const coverageTotalsResponse = z.object({
  data: z
    .object({
      covered_tactics: z.number().default(0),
      covered_techniques: z.number().default(0),
      total_tactics: z.number().default(0),
      total_techniques: z.number().default(0),
    })
    .default({}),
  status: z.string().optional(),
})

/** A miss is `{ result: {}, status: 'success' }` — every field must default. */
export const techniqueDetailResponse = z.object({
  result: z
    .object({
      technique_id: z.string().default(''),
      technique_name: z.string().default(''),
      technique_description: z.string().nullable().default(null),
      technique_detection: z.string().nullable().default(null),
      technique_tactics: z.string().nullable().default(null),
      technique_platforms: z.string().nullable().default(null),
      data_sources: z.string().nullable().default(null),
      defenses_bypassed: z.string().nullable().default(null),
      sub_technique: z.string().default('0'),
    })
    .passthrough()
    .default({}),
  status: z.string().optional(),
})

export type CoverageTotals = z.output<typeof coverageTotalsResponse>['data']
export type TechniqueDetail = z.output<typeof techniqueDetailResponse>['result']

/* ------------------------------------------------------------------ helpers */

/**
 * Fold the observed tactic-name dirt: case variants and the British
 * spelling. Returns the canonical display form used for joining onto the
 * skeleton — matching is by the folded key, display uses the skeleton's own
 * spelling wherever the skeleton knows the tactic.
 */
export function tacticKey(name: string): string {
  return name.trim().toLowerCase().replace(/defence/g, 'defense')
}

/** Every stream a detection reads, flattened — group semantics unverified. */
export function detectionStreams(d: Detection): string[] {
  return [...new Set(d.requires_stream.flat().map((s) => s.stream))]
}

/* ---------------------------------------------------------------------- api */

export interface AttackScope {
  clusterId: string
  scopeId: string
}

export interface AttackApi {
  /** The tactic-name skeleton, in the server's order. */
  tactics: (q: AttackScope) => Promise<string[]>
  /** Technique names under one tactic — POST attack/matrix {tactic}. */
  techniques: (q: AttackScope & { tactic: string }) => Promise<string[]>
  /** Technique name -> detection content. ~2.4MB live; call once, cache. */
  analytics: (q: AttackScope) => Promise<Record<string, Detection[]>>
  /** The posture denominators — the only source of the uncovered count. */
  totals: (q: AttackScope) => Promise<CoverageTotals>
  /** Framework detail by ATT&CK T-id. Returns null on the empty-result miss. */
  technique: (q: AttackScope & { techniqueId: string }) => Promise<TechniqueDetail | null>
}

export function createAttackApi(client: ApiClient): AttackApi {
  return {
    tactics: async ({ clusterId, scopeId }) => {
      const res = await client.request(
        'GET',
        `/${clusterId}/wrk/api/attack/matrix?scope_id=${encodeURIComponent(scopeId)}`,
        { schema: attackTacticsResponse },
      )
      return res.result
    },

    techniques: async ({ clusterId, scopeId, tactic }) => {
      const res = await client.request('POST', `/${clusterId}/wrk/api/attack/matrix`, {
        body: { tactic, scope_id: scopeId },
        schema: attackTacticsResponse,
      })
      return res.result.map((t) => t.trim()).filter(Boolean)
    },

    analytics: async ({ clusterId, scopeId }) => {
      const res = await client.request<unknown>('POST', `/${clusterId}/wrk/api/attack/analytics`, {
        body: { scope_id: scopeId },
        rawEnvelope: true,
      })
      return readAnalytics(res)
    },

    totals: async ({ clusterId, scopeId }) => {
      const res = await client.request('POST', `/${clusterId}/api/landingpage/coverage`, {
        body: { scope_id: scopeId },
        schema: coverageTotalsResponse,
      })
      return res.data
    },

    technique: async ({ clusterId, scopeId, techniqueId }) => {
      const res = await client.request('POST', `/${clusterId}/wrk/api/get_mitre_technique_data`, {
        body: { scope_id: scopeId, technique_id: techniqueId },
        schema: techniqueDetailResponse,
      })
      return res.result.technique_id ? res.result : null
    },
  }
}
